<script setup lang="ts">
import AccordionComponent from "@/components/accordion/AccordionComponent.vue";
import { defineProps, inject, ref, defineModel, watch } from "vue";
import { DestinyMatrix, DestinyMatrixKeysEnum } from "@/types/destinyMatrix";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  matrix: DestinyMatrix | undefined;
  hasPremiumAccess: boolean;
}>();
const isOpen = defineModel<boolean>({ default: false });
const getDescription =
  inject<(key: DestinyMatrixKeysEnum) => Promise<never>>("getDescription");

const { t } = useI18n();

watch(
  () => props.matrix,
  () => (description.value = undefined)
);

const description = ref();

async function getData() {
  if (!props.hasPremiumAccess) {
    isOpen.value = false;
  }
  if (!description.value && props.matrix && getDescription) {
    description.value = await getDescription(DestinyMatrixKeysEnum.PURPOSES);
  }
}
</script>

<template>
  <AccordionComponent
    type="white"
    @opened="getData"
    v-model="isOpen"
    :isBlocked="!hasPremiumAccess"
  >
    <template #title>
      {{ t("calculators.destiny-matrix.accordions.purposes.title") }}
    </template>
    <template #faq-content>
      <div v-if="description" class="flex flex-col gap-2">
        <p class="text-xl font-bold pt-4">
          {{
            t("calculators.destiny-matrix.accordions.purposes.personal-title", {
              first: matrix.personal.h,
              second: matrix.personal.e,
              third: matrix.personal.social,
            })
          }}
        </p>
        <div class="whitespace-pre-line">
          {{
            t("calculators.destiny-matrix.accordions.purposes.personal-desc")
          }}
        </div>
        <div
          v-for="(personal, index) in description['personal']"
          class="whitespace-pre-line"
          :key="'personal' + index"
        >
          <p class="mb-2 font-bold">
            {{
              t("calculators.destiny-matrix.accordions.purposes.arcane-title", {
                value: index,
              })
            }}
          </p>
          <p>{{ personal }}</p>
        </div>

        <p class="text-xl font-bold pt-4">
          {{
            t("calculators.destiny-matrix.accordions.purposes.social-title", {
              first: matrix.personal.m,
              second: matrix.personal.f,
              third: matrix.personal.personal,
            })
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ t("calculators.destiny-matrix.accordions.purposes.social-desc") }}
        </div>
        <div
          v-for="(social, index) in description['social']"
          class="whitespace-pre-line"
          :key="'social-' + index"
        >
          <p class="mb-2 font-bold">
            {{
              t("calculators.destiny-matrix.accordions.purposes.arcane-title", {
                value: index,
              })
            }}
          </p>
          <p>{{ social }}</p>
        </div>

        <p class="text-xl font-bold pt-4">
          {{
            t("calculators.destiny-matrix.accordions.purposes.spirit-title", {
              value: matrix.personal.harmony,
            })
          }}
        </p>
        <div class="whitespace-pre-line">
          {{ t("calculators.destiny-matrix.accordions.purposes.spirit-desc") }}
        </div>
        <div class="whitespace-pre-line font-bold">
          {{
            t("calculators.destiny-matrix.accordions.purposes.spirit-subtitle")
          }}
        </div>
        <div class="whitespace-pre-line">
          {{ description["spirit"] }}
        </div>
      </div>
    </template>
    <template #collapse-text-btn>
      {{ t("calculators.destiny-matrix.accordions.collapse") }}
    </template>
  </AccordionComponent>
</template>
